import React, { forwardRef, useEffect, useRef } from 'react';
import { RefCallBack } from 'react-hook-form';

import cx from 'clsx';

import { Input, InputProps } from '../Input/Input';

import styles from './AuthCodeInput.module.scss';

export type AuthCodeInputProps = InputProps & {
  allowedCharacters?: RegExp | string;
  characters?: number;
  containerClassName?: string;
  inputClassName?: string;
  inputType?: 'number' | 'password' | 'text';
  onChange?: (res: string) => void;
};

export const AuthCodeInput = forwardRef<HTMLInputElement, AuthCodeInputProps>(
  (
    {
      allowedCharacters = '^[A-Za-z0-9]*$',
      characters = 6,
      containerClassName,
      inputClassName,
      inputType = 'text',
      onChange,
      disabled,
      valid,
    },
    ref
  ) => {
    const inputsRef = useRef<Array<HTMLInputElement>>([]);

    useEffect(() => {
      inputsRef.current[0].focus();
    }, []);

    const sendResult = () => {
      const res = inputsRef.current.map((input) => input.value).join('');

      onChange && onChange(res);
    };

    const handleOnChange = (targetInputIdx: number) => {
      const targetInput = inputsRef.current[targetInputIdx];

      if (targetInput.value.match(allowedCharacters)) {
        const nextSibling = inputsRef.current[targetInputIdx + 1];

        nextSibling && nextSibling.focus();
      } else {
        targetInput.value = '';
      }

      sendResult();
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, targetInputIdx: number) => {
      const { key } = e;
      const targetInput = inputsRef.current[targetInputIdx];
      const previousSibling = inputsRef.current[targetInputIdx - 1];

      if (key === 'Backspace') {
        if (targetInput.value === '' && previousSibling) {
          previousSibling.focus();
          e.preventDefault();
        } else {
          targetInput.value = '';
        }

        sendResult();
      } else {
        setTimeout(() => {
          handleOnChange(targetInputIdx);
        });
      }
    };

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const value = e.clipboardData.getData('Text');

      if (value.match(allowedCharacters)) {
        for (let i = 0; i < characters && i < value.length; i++) {
          inputsRef.current[i].value = value.charAt(i);
          const nextSibling = inputsRef.current[i + 1];

          nextSibling && nextSibling.focus();
        }

        sendResult();
      }

      e.preventDefault();
    };

    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    const inputs = [];

    for (let i = 0; i < characters; i++) {
      inputs.push(
        <Input
          key={`auth_code_${i}`}
          name={`auth_code_${i}`}
          onKeyDown={(e) => handleOnKeyDown(e, i)}
          onFocus={handleOnFocus}
          onPaste={handleOnPaste}
          valid={valid}
          type={inputType}
          ref={(el: HTMLInputElement) => {
            // To set focus on the first character if validation fails
            if (i === 0) {
              (ref as RefCallBack)?.(el);
            }

            inputsRef.current[i] = el;
          }}
          maxLength={1}
          disabled={disabled}
          className={cx(styles['auth-code__input'], inputClassName)}
          data-testid="auth-code-input-component"
        />
      );
    }

    return (
      <div
        className={cx(styles['auth-code__container'], containerClassName)}
        data-testid="auth-code-container-component"
        ref={ref}
      >
        {inputs}
      </div>
    );
  }
);

AuthCodeInput.displayName = 'AuthCodeInput';
